import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { authPostRequest } from "../../api/authClient";

const MIN_PASSWORD_LEN = 8;

function signInHref(): string {
  const raw = (process.env.REACT_APP_LOGIN_URL || "http://localhost:4702").trim();
  const base = raw.replace(/\/$/, "");
  if (/\/login$/i.test(base)) return base;
  return `${base}/login`;
}

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!token) {
      setError("Missing setup token. Open the link from your verification email.");
      return;
    }
    if (password.length < MIN_PASSWORD_LEN) {
      setError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await authPostRequest("/client/auth/setup-password", {
        token,
        password,
      });
      setMessage("Password set successfully. Redirecting to sign in…");
      setTimeout(() => {
        window.location.href = signInHref();
      }, 1500);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as { message?: string })?.message;
      setError(
        typeof msg === "string" && msg.length > 0
          ? msg
          : "Could not set password. The link may have expired.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-slate-50">Set your password</h2>
        <p className="text-sm text-slate-400 mt-1">
          Your email is verified. Choose a password for your Variphi account.
        </p>
      </div>

      {!token ? (
        <div className="rounded-lg border border-amber-800/60 bg-amber-950/40 px-4 py-3 text-sm text-amber-100">
          This page needs a valid token from your verification email.
        </div>
      ) : null}

      <form className="space-y-4" onSubmit={onSubmit}>
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label htmlFor="niadit-set-password" className="text-xs font-medium text-slate-300">
              Password
            </label>
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="text-xs text-sky-400 hover:text-sky-300"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <input
            id="niadit-set-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD_LEN}
            autoComplete="new-password"
            disabled={!token}
            className="w-full rounded-lg border border-slate-700 bg-slate-950/50 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:opacity-50"
            placeholder={`At least ${MIN_PASSWORD_LEN} characters`}
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label htmlFor="niadit-set-confirm" className="text-xs font-medium text-slate-300">
              Confirm password
            </label>
            <button
              type="button"
              onClick={() => setShowConfirm((s) => !s)}
              className="text-xs text-sky-400 hover:text-sky-300"
            >
              {showConfirm ? "Hide" : "Show"}
            </button>
          </div>
          <input
            id="niadit-set-confirm"
            type={showConfirm ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD_LEN}
            autoComplete="new-password"
            disabled={!token}
            className="w-full rounded-lg border border-slate-700 bg-slate-950/50 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:opacity-50"
            placeholder="Re-enter password"
          />
        </div>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}
        {message ? <p className="text-sm text-emerald-400">{message}</p> : null}

        <button
          type="submit"
          disabled={isLoading || !token}
          className="w-full rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? "Setting password…" : "Set password"}
        </button>
      </form>

      <p className="text-center text-sm text-slate-400">
        <a href={signInHref()} className="font-medium text-sky-400 hover:text-sky-300">
          Back to sign in
        </a>
      </p>
    </div>
  );
}
